import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { RiskBadge } from "@/components/RiskBadge";
import StatusStepper from "@/components/StatusStepper";
import { statusColor } from "@/lib/constants";
import {
  Loader2, ArrowLeft, Dna, Mic, TrendingUp, FileText, Stethoscope, X, AlertTriangle,
  FileDown, AudioLines,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const RISK_NUM = { Mild: 1, Moderate: 2, High: 3 };

export default function PatientProfile() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [active, setActive] = useState(null); // condition id detail panel
  const [reviewFor, setReviewFor] = useState(null); // test object

  const load = async () => {
    const { data } = await api.get(`/profile/patient/${id}`);
    setProfile(data);
    if (!active && data.layers.length) setActive(data.layers[0].condition.id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!profile) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const layer = profile.layers.find((l) => l.condition.id === active);

  const setStatus = async (testId, status) => {
    try { await api.post(`/genetic/${testId}/status`, { status }); await load(); toast.success(`Status → ${status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <Link to="/clinician" className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-brand mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to patients
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Integrated profile · <span className="mono text-brand">{profile.patient.manogene_id}</span></div>
          <h1 className="font-head text-3xl font-bold text-ink mt-1">{profile.patient.name}</h1>
          <p className="text-ink-soft mt-1">{profile.patient.email} · Consent: <span className="font-medium">{profile.patient.consent_status}</span></p>
        </div>
        <div className="flex gap-3 items-stretch">
          <a href={`${API}/profile/${id}/export?auth=${getToken()}`} target="_blank" rel="noreferrer" data-testid="export-pdf-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-brand text-white px-4 py-2 font-semibold hover:bg-brand-hover transition-colors">
            <FileDown className="h-4 w-4" /> Export PDF
          </a>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-center">
            <div className="mono text-xl font-semibold text-ink">{profile.sessions.length}</div>
            <div className="eyebrow">Sessions</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-center">
            <div className="mono text-xl font-semibold text-red-600">{profile.escalations.length}</div>
            <div className="eyebrow">Escalations</div>
          </div>
        </div>
      </div>

      {profile.escalations.length > 0 && (
        <div className="mt-5 rounded-xl border border-red-200 bg-[#FEF2F2] px-4 py-3 flex items-start gap-2" data-testid="profile-escalations">
          <TrendingUp className="h-4 w-4 text-red-600 mt-0.5" />
          <div className="text-sm text-red-700">
            <span className="font-semibold text-[#B91C1C]">Longitudinal escalations: </span>
            {profile.escalations.map((e) => `${e.condition.name} ${e.from}→${e.to}`).join(" · ")}
          </div>
        </div>
      )}

      {/* Condition selector */}
      <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
        {profile.layers.map((l) => {
          const isStroke = l.condition.id === "stroke";
          return (
            <button key={l.condition.id} onClick={() => setActive(l.condition.id)}
              data-testid={`cond-tab-${l.condition.id}`}
              className={`shrink-0 rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors ${
                active === l.condition.id ? "bg-brand text-white border-brand" : "bg-white text-ink-soft border-slate-200 hover:bg-slate-50"
              } ${isStroke ? "ring-1 ring-offset-1 ring-blue-300" : ""}`}>
              {l.condition.name}
              {l.voice_layer.escalation && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-red-500" />}
            </button>
          );
        })}
      </div>

      {/* Two-layer grid */}
      {layer && (
        <div className="mt-4 grid lg:grid-cols-12 gap-5" data-testid="two-layer-profile">
          {/* Voice layer */}
          <div className="lg:col-span-4 rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-brand mb-4">
              <Mic className="h-4.5 w-4.5 h-[18px] w-[18px]" />
              <span className="font-head font-semibold">Voice Layer</span>
            </div>
            <div className="eyebrow">{layer.condition.name} · {layer.condition.category}</div>
            <div className="mt-2 flex items-center gap-3">
              <RiskBadge risk={layer.voice_layer.latest?.risk} testId="profile-voice-risk" />
              {layer.voice_layer.latest && (
                <span className="mono text-xs text-ink-muted">confidence {layer.voice_layer.latest.confidence}</span>
              )}
            </div>
            {layer.voice_layer.escalation && (
              <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5" /> Escalated {layer.voice_layer.escalation.from} → {layer.voice_layer.escalation.to}
              </div>
            )}
            {layer.voice_layer.history.length > 1 && (
              <div className="mt-4">
                <div className="eyebrow mb-1">Longitudinal trend</div>
                <div className="h-28">
                  <ResponsiveContainer width="100%" height={112} minWidth={0}>
                    <LineChart data={layer.voice_layer.history.map((h, i) => ({ i: i + 1, v: RISK_NUM[h.risk], risk: h.risk }))}>
                      <Line type="monotone" dataKey="v" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 3 }} />
                      <YAxis domain={[0.5, 3.5]} ticks={[1, 2, 3]} tickFormatter={(v) => ["", "Mild", "Mod", "High"][v]} width={38} tick={{ fontSize: 11 }} />
                      <XAxis dataKey="i" tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v, n, p) => p.payload.risk} labelFormatter={(l) => `Session ${l}`} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            <p className="mt-4 text-xs text-ink-muted">Raw acoustic features are hidden. Triage reflects model output only.</p>
          </div>

          {/* Genetic layer */}
          <div className="lg:col-span-8 rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-brand">
                <Dna className="h-[18px] w-[18px]" />
                <span className="font-head font-semibold">Genetic Layer</span>
              </div>
              {layer.genetic_layer && (
                <StatusPillBig status={layer.genetic_layer.status} />
              )}
            </div>

            {layer.genetic_layer ? (
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <div className="eyebrow mb-2">Mapped genetic tests</div>
                  <div className="space-y-2">
                    {(layer.genetic_layer.recommended_tests || []).map((t, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                        <span className="font-medium text-ink">{t.test}</span>
                        <span className="mono text-xs text-brand">{t.gene}</span>
                      </div>
                    ))}
                  </div>

                  {(layer.genetic_layer.address || layer.genetic_layer.collection_date) && (
                    <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-xs text-ink-soft space-y-1">
                      {layer.genetic_layer.address && (
                        <div><span className="font-semibold text-ink">Home collection address:</span>{" "}
                          {[layer.genetic_layer.address.line1, layer.genetic_layer.address.line2,
                            layer.genetic_layer.address.city, layer.genetic_layer.address.state,
                            layer.genetic_layer.address.postal_code, layer.genetic_layer.address.country]
                            .filter(Boolean).join(", ")}
                        </div>
                      )}
                      {layer.genetic_layer.collection_date && (
                        <div><span className="font-semibold text-ink">Preferred date:</span> {layer.genetic_layer.collection_date}</div>
                      )}
                    </div>
                  )}

                  {layer.genetic_layer.report_file_id && (
                    <a href={`${API}/genetic/report/${layer.genetic_layer.report_file_id}?auth=${getToken()}`} target="_blank" rel="noreferrer"
                      data-testid="view-report-link"
                      className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-ink hover:bg-slate-50 transition-colors">
                      <FileText className="h-4 w-4" /> View lab report (PDF)
                    </a>
                  )}

                  {/* Contradiction note */}
                  {layer.genetic_layer.report_file_id && layer.voice_layer.latest && (
                    <div className="mt-3 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-brand flex items-start gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                      A genetic finding is never auto-converted to a diagnosis. Compare against voice triage ({layer.voice_layer.latest.risk}) before deciding.
                    </div>
                  )}
                </div>

                <div>
                  <div className="eyebrow mb-2">Workflow status</div>
                  <div className="max-h-72 overflow-y-auto pr-1">
                    <StatusStepper status={layer.genetic_layer.status} />
                  </div>
                </div>

                <div className="md:col-span-2 border-t border-slate-100 pt-4 flex flex-wrap gap-2">
                  {layer.genetic_layer.status === "In Lab" && (
                    <span className="text-sm text-ink-muted">Sample with lab — awaiting report upload.</span>
                  )}
                  {layer.genetic_layer.status === "Report Available" && (
                    <button onClick={() => setReviewFor(layer.genetic_layer)} data-testid="review-test-btn"
                      className="inline-flex items-center gap-2 rounded-lg bg-brand text-white px-4 py-2 text-sm font-semibold hover:bg-brand-hover transition-colors">
                      <Stethoscope className="h-4 w-4" /> Write interpretation
                    </button>
                  )}
                  {layer.genetic_layer.clinician_review && (
                    <div className="w-full rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                      <div className="font-semibold text-ink">Interpretation — {layer.genetic_layer.clinician_review.decision}</div>
                      <p className="text-ink-soft mt-1">{layer.genetic_layer.clinician_review.interpretation}</p>
                      <div className="mono text-xs text-ink-muted mt-1">
                        {layer.genetic_layer.clinician_review.reviewer_name} · {new Date(layer.genetic_layer.clinician_review.reviewed_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-ink-muted">No genetic test record.</p>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="voice-recordings">
        <div className="flex items-center gap-2 text-brand mb-4">
          <AudioLines className="h-[18px] w-[18px]" />
          <span className="font-head font-semibold">Voice Recordings</span>
          <span className="ml-2 text-xs text-ink-muted">{profile.sessions.length} session(s)</span>
        </div>
        {profile.sessions.length === 0 ? (
          <p className="text-sm text-ink-muted">No voice sessions recorded.</p>
        ) : (
          <div className="space-y-3">
            {profile.sessions.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 px-4 py-3">
                <div className="min-w-[180px]">
                  <div className="text-sm font-medium text-ink">{new Date(s.created_at).toLocaleString()}</div>
                  <div className="mono text-xs text-ink-muted">
                    {Math.round(s.duration_seconds)}s · {s.quality_pass ? "quality ok" : "rejected"} · {s.ai_status}
                  </div>
                </div>
                <div className="flex-1 min-w-[220px]">
                  {s.file_id ? (
                    <audio controls preload="none" className="w-full h-9" data-testid={`audio-${s.id}`}
                      src={`${API}/voice/audio/${s.id}?auth=${getToken()}`} />
                  ) : (
                    <span className="text-xs text-ink-muted italic">No stored audio for this session (seed/demo).</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {reviewFor && <ReviewModal test={reviewFor} onClose={() => setReviewFor(null)} onSaved={async () => { setReviewFor(null); await load(); }} />}
    </div>
  );
}

function StatusPillBig({ status }) {
  const c = statusColor(status);
  return (
    <span className="inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-semibold mono"
      style={{ background: c.bg, color: c.text, borderColor: c.border }} data-testid="genetic-status-pill">
      {status}
    </span>
  );
}

function ReviewModal({ test, onClose, onSaved }) {
  const [interpretation, setInterpretation] = useState(test.clinician_review?.interpretation || "");
  const [decision, setDecision] = useState(test.clinician_review?.decision || "Consistent with voice triage");
  const [complete, setComplete] = useState(true);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!interpretation.trim()) return toast.error("Interpretation required");
    setBusy(true);
    try {
      await api.post(`/genetic/${test.id}/review`, { interpretation, decision, complete });
      toast.success("Interpretation saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head text-xl font-semibold text-ink">Clinical interpretation</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        <label className="block mb-3">
          <span className="text-sm font-medium text-ink-soft">Decision</span>
          <select value={decision} onChange={(e) => setDecision(e.target.value)} data-testid="review-decision"
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-brand">
            <option>Consistent with voice triage</option>
            <option>Contradicts voice triage</option>
            <option>Inconclusive — further testing</option>
            <option>No clinically significant finding</option>
          </select>
        </label>
        <label className="block mb-3">
          <span className="text-sm font-medium text-ink-soft">Interpretation notes</span>
          <textarea value={interpretation} onChange={(e) => setInterpretation(e.target.value)} rows={5} data-testid="review-notes"
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none focus:border-brand"
            placeholder="Document your clinical interpretation. Genetic findings are not auto-diagnoses." />
        </label>
        <label className="flex items-center gap-2 mb-5 text-sm text-ink-soft">
          <input type="checkbox" checked={complete} onChange={(e) => setComplete(e.target.checked)} className="h-4 w-4 accent-[#1E3A8A]" data-testid="review-complete" />
          Mark review as completed
        </label>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-lg border border-slate-300 py-2.5 font-semibold text-ink-soft hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="review-save"
            className="flex-1 rounded-lg bg-brand text-white py-2.5 font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Save interpretation"}
          </button>
        </div>
      </div>
    </div>
  );
}
